import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { User } from '../types';
import { AccessArea, AccessLevel, AccessMap, atLeast, defaultAccess } from '../access/areas';

export interface Account {
  _id: string;
  userId: string;
  companyId: string | null;
  companyName?: string;
  role: string;
  status: string;
  serviceAreaId?: number;
  employeeId?: string;
  permissions?: string[];
  customPermissions?: Record<string, boolean>;
}

interface AuthState {
  user: User | null;
  account: Account | null;
  accounts: Account[];
  token: string | null;
  isAuthenticated: boolean;
  setAuth: (user: User, token: string, account?: Account) => void;
  /**
   * Merge changes into the signed-in user.
   *
   * Nothing used to write back here. Settings PUT the profile, then called
   * its own `fetchUserInfo()`, which short-circuits to this store — so a
   * saved name visibly reverted to the old one, and a newly uploaded photo
   * never reached the topbar at all. Anything that changes the user calls
   * this.
   */
  updateUser: (patch: Partial<User>) => void;
  setAccounts: (accounts: Account[]) => void;
  selectAccount: (accountId: string) => void;
  clearAuth: () => void;
  hasPermission: (permission: string) => boolean;
  /**
   * What this person may open, from GET /api/access/me (null until it
   * arrives — the role's defaults stand in meanwhile).
   */
  access: AccessMap | null;
  setAccess: (access: AccessMap | null) => void;
  /** May this person reach `level` in `area`? What to SHOW; the server decides. */
  canArea: (area: AccessArea, level?: AccessLevel) => boolean;
  /** Limited to their own service area in `area`? */
  areaScoped: (area: AccessArea) => boolean;
  initializeAuth: () => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      account: null,
      accounts: [],
      token: null,
      isAuthenticated: false,
      access: null,

      setAuth: (user: User, token: string, account?: Account) => {
        // A different account is a different answer: drop the old one so
        // the role's defaults stand in until the new one arrives.
        const switched = get().account?._id !== account?._id;
        set({ user, token, account, isAuthenticated: true, ...(switched ? { access: null } : {}) });
        localStorage.setItem('token', token);
      },

      updateUser: (patch: Partial<User>) => {
        const { user } = get();
        if (!user) return;
        set({ user: { ...user, ...patch } });
      },

      setAccounts: (accounts: Account[]) => {
        set({ accounts });
      },

      selectAccount: (accountId: string) => {
        const { accounts } = get();
        const selectedAccount = accounts.find(acc => acc._id === accountId);
        if (selectedAccount) {
          set({ account: selectedAccount });
        }
      },

      clearAuth: () => {
        set({ user: null, account: null, accounts: [], token: null, isAuthenticated: false, access: null });
        localStorage.removeItem('token');
      },

      // Reconcile the two places a token can live, on boot.
      //
      // This used to only ever turn authentication ON: if the bare
      // `token` key was present and a user had been persisted, it set
      // `isAuthenticated: true`. It could not turn it OFF, so a session
      // the API had already rejected — which clears `token` — rehydrated
      // from `auth-storage` still claiming to be signed in. The login
      // screen then bounced to /dashboard, which 401'd, which reloaded
      // the login screen, for ever.
      //
      // It now decides both ways, and the store's token is the one that
      // counts: it is what the request interceptor sends.
      initializeAuth: () => {
        const state = get();
        const token = state.token || localStorage.getItem('token');

        if (!token || !state.user) {
          if (state.isAuthenticated || state.token) get().clearAuth();
          return;
        }

        // Keep the bare key in step for the two callers that still read it
        // directly (the Mapbox fetch in CoverageMap).
        localStorage.setItem('token', token);
        set({ token, isAuthenticated: true });
      },

      setAccess: (access: AccessMap | null) => set({ access }),

      canArea: (area: AccessArea, level: AccessLevel = 'view'): boolean => {
        const { account, access } = get();
        if (!account) return false;
        const map = access || defaultAccess(account.role);
        return atLeast(map[area]?.level, level);
      },

      areaScoped: (area: AccessArea): boolean => {
        const { account, access } = get();
        const map = access || defaultAccess(account?.role);
        return map[area]?.scope === 'area';
      },

      hasPermission: (permission: string): boolean => {
        const { account } = get();
        if (!account) return false;

        // The role decides, nothing else. This used to trust
        // `account.permissions` and `account.customPermissions` first —
        // fields any user could write onto their own account through the
        // API, so a BC could unlock Payroll's screens by setting one. The
        // server no longer accepts them (2026-10-24); the access matrix
        // will send what each person may open.
        //
        // Default role-based permissions
        const rolePermissions: Record<string, string[]> = {
          SA: ['*'], // System Admin has all permissions
          CA: ['*'], // Company Admin has all permissions
          AO: ['*'], // Admin has all permissions
          BC: ['tasks.view', 'tasks.create', 'tasks.edit', 'dashboard.view', 'reports.view'],
          Driver: ['tasks.view', 'dashboard.view'],
          Maintainer: ['tasks.view', 'dashboard.view'],
        };

        const accountRolePermissions = rolePermissions[account.role] || [];
        
        // Check if role has wildcard (all permissions)
        if (accountRolePermissions.includes('*')) {
          return true;
        }

        return accountRolePermissions.includes(permission);
      },
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        user: state.user,
        account: state.account,
        accounts: state.accounts,
        token: state.token,
        isAuthenticated: state.isAuthenticated,
        access: state.access,
      }),
    }
  )
);
